/**
 * Provider logos — the exact same SVG glyphs the chatbot edit page uses in
 * its model selector. Single source of truth, single visual style across
 * the platform (model selector, cost simulator, tenant API-keys panel).
 *
 * Glyphs sourced from frontend/src/modules/chatbots/components/BotModal.tsx
 * `getProviderIcon()`. The chip background/tint styles live in
 * BotModal.css under `.model-provider-icon.<provider>`.
 */
interface Props {
  provider: string;
  size?: number;
}

export function ProviderIcon({ provider, size = 14 }: Props) {
  const p = (provider || '').toLowerCase();

  if (p === 'anthropic') {
    return (
      <span className="model-provider-icon anthropic" title="Anthropic" style={{ display: 'inline-flex' }}>
        <svg viewBox="0 0 46 32" width={size} height={Math.round(size * 10 / 14)} fill="currentColor">
          <path d="M32.73 0h-6.945L38.45 32h6.945L32.73 0ZM13.27 0 0 32h7.082l2.79-7.345h13.144l2.79 7.345h7.082L19.607 0h-6.338Zm-.903 18.46 4.222-11.12 4.222 11.12H12.367Z" />
        </svg>
      </span>
    );
  }
  if (p === 'openai') {
    return (
      <span className="model-provider-icon openai" title="OpenAI" style={{ display: 'inline-flex' }}>
        <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
          <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
        </svg>
      </span>
    );
  }
  if (p === 'google') {
    return (
      <span className="model-provider-icon google" title="Google" style={{ display: 'inline-flex' }}>
        <svg viewBox="0 0 24 24" width={size} height={size}>
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      </span>
    );
  }
  if (p === 'moonshot' || p === 'kimi') {
    return (
      <span className="model-provider-icon moonshot" title="Moonshot (Kimi)" style={{ display: 'inline-flex' }}>
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
          <circle cx="12" cy="12" r="11" fill="#6D5DFC" />
          <path d="M16 6.5a7 7 0 1 0 1.5 9.5A8 8 0 0 1 16 6.5Z" fill="#ffffff" />
        </svg>
      </span>
    );
  }
  return null;
}
